// Proficiency-scale palette — promoted from analyse's lib/palette.ts on its
// second consumer (gradebook). Difficulty helpers stayed analyse-local.
import { useEffect, useState } from 'react';

import { getToken, onThemeChange, type TokenName } from '@/lib/tokens';

export type PaletteVariant = 'summary' | 'standards';

const SUMMARY_SLOTS = ['below-basic', 'basic', 'proficient', 'advanced'] as const;
const STANDARDS_SLOTS = ['below-basic', 'basic', 'near-mastery', 'mastery'] as const;

export interface GradingScaleLevel {
  label: string;
  threshold?: number;
  color?: string;
}

export const slotForIndex = (index: number, variant: PaletteVariant = 'standards'): string => {
  const slots = variant === 'summary' ? SUMMARY_SLOTS : STANDARDS_SLOTS;
  if (index < 0) return slots[0];
  if (index >= slots.length) return slots[slots.length - 1];
  return slots[index];
};

const SLOT_TOKEN: Record<string, TokenName> = {
  'below-basic': 'destructive',
  basic: 'apWarning',
  'near-mastery': 'chart1',
  proficient: 'chart1',
  mastery: 'apSuccess',
  advanced: 'apSuccess',
};

export const slotColor = (slot: string): string =>
  getToken(SLOT_TOKEN[slot] ?? 'destructive') || getToken('destructive');

// Tailwind chip classes per slot (tinted bg + strong fg) — chips, matrix cells, KPI accents.
const SLOT_CHIP_CLASSES: Record<string, string> = {
  'below-basic': 'bg-ap-destructive-a10 text-destructive',
  basic: 'bg-ap-warning-a15 text-ap-warning',
  'near-mastery': 'bg-ap-chart1-a15 text-chart-1',
  proficient: 'bg-ap-chart1-a15 text-chart-1',
  mastery: 'bg-ap-success-a15 text-ap-success',
  advanced: 'bg-ap-success-a15 text-ap-success',
};

export const slotChipClasses = (slot: string): string =>
  SLOT_CHIP_CLASSES[slot] ?? SLOT_CHIP_CLASSES['below-basic'];

export const colorForLevel = (
  level: GradingScaleLevel,
  index: number,
  variant: PaletteVariant = 'standards'
): string => {
  if (level.color && level.color.trim().length > 0) return level.color;
  return slotColor(slotForIndex(index, variant));
};

export const indexOfLabel = (
  label: string | null | undefined,
  levels: ReadonlyArray<GradingScaleLevel> | undefined
): number => {
  if (!label || !levels || levels.length === 0) return -1;
  return levels.findIndex((lvl) => lvl.label === label);
};

// Levels arrive in DESCENDING threshold order — walk top-down, first cleared threshold wins.
export const bandForPct = (
  pct: number | null | undefined,
  levels: ReadonlyArray<GradingScaleLevel> | undefined
): string | null => {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  if (!levels || levels.length === 0) return null;
  return (
    levels.find((level) => level.threshold !== undefined && pct >= level.threshold)?.label ?? null
  );
};

// Upper bound of a level = the PREVIOUS (higher) level's threshold; top level renders ">N%".
export const formatLevelRange = (
  level: GradingScaleLevel,
  index: number,
  levels: ReadonlyArray<GradingScaleLevel>
): string => {
  const lower = level.threshold;
  if (lower === undefined || lower === null) return '';
  const upper = levels[index - 1]?.threshold;
  if (upper === undefined || upper === null) return `>${lower}%`;
  return `${lower}-${upper}%`;
};

// Alpha-composes a modern space-separated CSS color (our tokens are oklch).
// System colors (forced-colors HC) and legacy comma syntax fall through solid.
export const withAlpha = (color: string, alpha: number): string => {
  const m = color.match(/^([a-z]+)\((.*?)(?:\s*\/\s*[^)]+)?\)$/i);
  if (m && !m[2].includes(',')) return `${m[1]}(${m[2]} / ${alpha})`;
  return color;
};

// Bump a memo key when the resolved theme flips so chart colors re-resolve.
export const useChartThemeTick = (): number => {
  const [tick, setTick] = useState(0);
  useEffect(() => onThemeChange(() => setTick((n) => n + 1)), []);
  return tick;
};
